import { useState } from "react"
import type { CSSProperties, ReactNode } from "react"

import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { PreviewTextDialog } from "./preview-text-dialog"

export function ColumnHeader({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("font-display flex h-12 items-center gap-2 px-3 font-bold", className)}>
      {children}
    </div>
  )
}

type ColumnCellProps = {
  children: ReactNode
  className?: string
  style?: CSSProperties
  filename?: string
  columnId?: string
} & ({ isPreviewable: true; text: string } | { isPreviewable?: false; text?: never })

export function ColumnCell({
  children,
  className,
  isPreviewable = false,
  text,
  filename,
  columnId,
  style,
}: ColumnCellProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const cellContent = (
    <div
      role={isPreviewable ? "button" : undefined}
      style={style}
      tabIndex={isPreviewable ? 0 : undefined}
      className={cn(
        "bg-muted/50 flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 transition-[background-color,box-shadow,scale] duration-150",
        isPreviewable && "hover:bg-muted cursor-pointer hover:shadow-sm active:scale-[0.99]",
        className
      )}
      onClick={isPreviewable ? () => setIsDialogOpen(true) : undefined}
      onKeyDown={
        isPreviewable
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                setIsDialogOpen(true)
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  )

  if (isPreviewable && text) {
    return (
      <>
        {cellContent}
        <PreviewTextDialog
          columnId={columnId}
          filename={filename}
          open={isDialogOpen}
          text={text}
          onOpenChange={setIsDialogOpen}
        >
          {text}
        </PreviewTextDialog>
      </>
    )
  }

  return cellContent
}

type HighlightedTextProps = {
  text: string
  searchTerm: string
}

export function HighlightedText({ text, searchTerm }: HighlightedTextProps) {
  if (!searchTerm.trim()) {
    return <>{text}</>
  }

  const lowerText = text.toLowerCase()
  const lowerSearchTerm = searchTerm.toLowerCase()
  const startIndex = lowerText.indexOf(lowerSearchTerm)

  if (startIndex === -1) {
    return <>{text}</>
  }

  const endIndex = startIndex + searchTerm.length
  const before = text.slice(0, startIndex)
  const match = text.slice(startIndex, endIndex)
  const after = text.slice(endIndex)

  return (
    <>
      {before}
      <mark className={cn("rounded-sm px-0.5", STATUS_TONES.warning.soft)}>{match}</mark>
      {after}
    </>
  )
}
